import { LoadingState } from "@repo/app-types/client/client";
import {
  renderTeammateDisplayName,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { MultiSelectPopover } from "@repo/ui/Select/multiSelectPopover";
import { selectStyle } from "@repo/ui/Select/style";
import { IconChevronDown } from "@tabler/icons-react";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { Button, Select } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Can } from "../../helpers/teammateAuthorizationContext.js";
import { useAppSelector } from "../../hooks/hook.js";
import { TeammateAvatarPile } from "../teammate/teammateAvatarPile.js";
import {
  selectAllTeammates,
  selectTeammatesLoadingState,
} from "../teammates/teammatesSlice.js";

export interface ProjectOwnersWidgetProps {
  teammateIds: readonly TeammateId[];
  onTeammateIdsSave: (teammateIds: readonly TeammateId[]) => Promise<void>;
}

const SAVE_DEBOUNCE_MS = 300;

export const ProjectOwnersWidget: FC<ProjectOwnersWidgetProps> = (props) => {
  const teammatesLoadingState = useAppSelector(selectTeammatesLoadingState);
  const teammates = useAppSelector((state) =>
    selectAllTeammates(state.teammates),
  );

  const [unsavedTeammateIds, setUnsavedTeammateIds] = useState<
    readonly TeammateId[] | undefined
  >(undefined);

  const teammateIds = unsavedTeammateIds ?? props.teammateIds;

  const onSave = useRef(props.onTeammateIdsSave);
  onSave.current = props.onTeammateIdsSave;

  // Tracks the latest unsaved edit so we can flush it synchronously on unmount.
  // Cleared once the debounced save fires (or when there's nothing to save).
  const pendingSaveRef = useRef<readonly TeammateId[] | null>(null);

  // Debounce save: when unsavedTeammateIds changes, schedule a save after
  // SAVE_DEBOUNCE_MS of inactivity. Resets if the user toggles again.
  useEffect(() => {
    if (!unsavedTeammateIds) {
      pendingSaveRef.current = null;
      return;
    }
    pendingSaveRef.current = unsavedTeammateIds;
    const handle = window.setTimeout(() => {
      pendingSaveRef.current = null;
      void onSave
        .current(unsavedTeammateIds)
        .finally(() =>
          setUnsavedTeammateIds((current) =>
            current === unsavedTeammateIds ? undefined : current,
          ),
        );
    }, SAVE_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [unsavedTeammateIds]);

  // Flush any pending save when the widget unmounts so navigating away within
  // the debounce window doesn't lose the user's edit.
  useEffect(
    () => () => {
      if (pendingSaveRef.current) {
        void onSave.current(pendingSaveRef.current);
        pendingSaveRef.current = null;
      }
    },
    [],
  );

  const sections = useMemo(
    () => [
      {
        id: "teammates",
        options: teammates.map((teammate) => ({
          id: teammate.id,
          label: renderTeammateDisplayName(teammate),
        })),
      },
    ],
    [teammates],
  );

  const value = useMemo(() => [...teammateIds], [teammateIds]);

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => {
        const isDisabled =
          !allowed || teammatesLoadingState !== LoadingState.LOADED;
        return (
          <Select
            aria-label="Project owners"
            className="relative contents"
            selectionMode="multiple"
            value={value}
            onChange={(next) => {
              setUnsavedTeammateIds(
                next.filter(
                  (key): key is TeammateId => typeof key === "string",
                ),
              );
            }}
            isDisabled={isDisabled}
          >
            <Button
              className={twMerge(
                selectStyle().button({
                  className:
                    "w-auto min-w-0 gap-x-1 rounded-full px-3 hover:bg-gray-50",
                }),
                !allowed && "opacity-50",
              )}
            >
              {teammateIds.length === 0 ? (
                <span className="truncate">Set owner</span>
              ) : (
                <>
                  <div className="flex h-5 shrink-0">
                    <TeammateAvatarPile
                      className="-my-1 -ml-2"
                      teammates={teammateIds.map((teammateId) =>
                        teammates.find(
                          (teammate) => teammate.id === teammateId,
                        ),
                      )}
                    />
                  </div>
                  <IconChevronDown
                    aria-hidden="true"
                    className="-mr-1 size-5 shrink-0 text-gray-400"
                  />
                </>
              )}
            </Button>
            <MultiSelectPopover sections={sections} popoverClassName="w-48" />
          </Select>
        );
      }}
    </Can>
  );
};
